import React, { useState } from 'react';
import { Button, Skeleton } from 'antd';
import classNames from 'classnames';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { IOTMOption, IOTMQuiz, usePostOTMQuizTest } from 'modules/otms-list';

interface IProps {
  onComplete: (data: any[]) => void;
  currentQuestionIndex: number;
  setCurrentQuestionIndex: React.Dispatch<React.SetStateAction<number>>;
  questions?: IOTMQuiz[];
  isLoading: boolean;
}

const OTMQuiz = ({ onComplete, currentQuestionIndex, setCurrentQuestionIndex, questions, isLoading }: IProps) => {
  const t = useTranslations();
  const { mutate } = usePostOTMQuizTest();

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const question = questions?.[currentQuestionIndex];
  const options: IOTMOption[] = question?.options || [];

  const handleOptionSelect = (optionId: string | undefined) => () => {
    if (optionId) {
      setSelectedOption(optionId);
    }
  };

  const handleNext = () => {
    if (!selectedOption) return;

    const updatedAnswers = [...selectedAnswers, selectedOption];

    const submitAnswers = async (answers: string[]) => {
      setIsSubmitting(true);
      mutate(answers, {
        onSuccess: data => {
          onComplete(data);
        },
        onSettled: () => {
          setIsSubmitting(false);
        }
      });
    };

    if (currentQuestionIndex === (questions?.length || 0) - 1) {
      submitAnswers(updatedAnswers);
    } else {
      setSelectedAnswers(updatedAnswers);
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:mt-5 md:grid-cols-4 md:gap-3">
      {/* LEFT SIDE */}
      <div className="hidden space-y-3 self-start p-4 sm:block md:sticky md:top-4 md:p-0">
        <div className="shadow-xs h-30 flex flex-col items-center justify-center space-y-2 rounded-xl bg-white bg-[url('/images/otm-banner-bg.webp')] bg-cover bg-center bg-no-repeat p-3 text-white">
          <p className="text-xs">{t('bilim_testi')}</p>
          <p className="text-center">{t('home.otm_rec_banner.main_title')}</p>
        </div>

        <div className="shadow-xs rounded-xl bg-white p-3">
          <h1 className="text-md font-medium ">{t('savollar')}</h1>
          <div className="my-2 flex items-center gap-2">
            <div className="flex-1 rounded-full border-2 border-[#C4C4C4] p-0.5">
              <div className="h-2 w-full rounded-full bg-gray-100">
                <div
                  className="h-2 rounded-full bg-green-500"
                  style={{
                    width: `${((currentQuestionIndex + 1) / (questions?.length || 15)) * 100}%`
                  }}
                />
              </div>
            </div>
            <div className="text-sm font-medium text-green-500">
              {currentQuestionIndex + 1}/{questions?.length || 0}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="p-4 sm:col-span-3 md:mt-0 md:p-0">
        {isLoading ? (
          <>
            <Skeleton active paragraph={{ rows: 4 }} />
          </>
        ) : (
          <>
            <div className="shadow-xs mb-3 rounded-xl bg-white px-4 py-3">
              <div className="text-md font-medium">{question?.name}</div>
            </div>

            <div className="shadow-xs mb-4 rounded-xl bg-white px-4 py-3">
              <div className="space-y-3">
                {options.map(option => {
                  const isSelected = selectedOption === option.id;

                  return (
                    <div
                      key={option.id}
                      className={classNames('rounded-xl border-2', {
                        'border-transparent': !isSelected,
                        'border-green-500 bg-green-50': isSelected
                      })}
                    >
                      <div
                        className={classNames(
                          'flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-all',
                          {
                            'border-gray-200': !isSelected,
                            'border-transparent': isSelected
                          }
                        )}
                        onClick={handleOptionSelect(option.id)}
                      >
                        {/* <h1 className="text-xl font-semibold text-gray-600">{String.fromCharCode(97 + index)})</h1> */}
                        <p className="text-sm text-gray-800">{option.name}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="fixed bottom-0 left-0 right-0 z-10 mt-5 flex h-20 items-center justify-between rounded-t-2xl bg-white px-4 sm:px-0 sm:static sm:h-auto sm:rounded-none sm:bg-transparent">
                {currentQuestionIndex !== (questions?.length || 0) - 1 && (
                  <Link href="/">
                    <Button className="!duration-400 !rounded-lg !border-none !bg-red-50 !px-8 !py-5 !font-medium !text-red-500 !transition !ease-in-out hover:!bg-red-500 hover:!text-white">
                      {t('yakunlash')}
                    </Button>
                  </Link>
                )}
                <div className="flex w-full justify-end">
                  <Button
                    disabled={!selectedOption || isSubmitting}
                    onClick={handleNext}
                    loading={isSubmitting}
                    className={classNames('!rounded-lg !border-none !px-8 !py-5 !font-medium', {
                      '!bg-green-500 !text-white': selectedOption,
                      '!bg-gray-100 !text-gray-400': !selectedOption
                    })}
                  >
                    {currentQuestionIndex === (questions?.length || 0) - 1 ? t('yakunlash') : t('keyingisi')}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OTMQuiz;
